import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  useTheme,
  useMediaQuery,
  FormControl,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Button,
} from '@mui/material';
import { AutoStories, DynamicFeed, CloudDone, CloudQueue } from '@mui/icons-material';
import { XIcon, StarIcon, GlobeIcon, LockIcon, NotePencilIcon, CaretRightIcon, TrashSimpleIcon } from '@phosphor-icons/react';
import Switch from '@mui/material/Switch';
import { tmdbService, TMDbMovie } from '../../services/tmdbApi';
import { User } from 'firebase/auth';
import CustomButton from '../widgets/CustomButton';
import BottomSheet, { BottomSheetDraggableHeader } from './BottomSheet';
import ImageShimmer from '../widgets/ImageShimmer';
import CompactMovieCard from '../widgets/CompactMovieCard';
import WatchedDateSelector, { WatchedDatePrecision } from '../widgets/WatchedDateSelector';
import { inferWatchedDatePrecision } from '@watchlist/shared';
import { glassStyles } from '../../tokens/designTokens';
import { font } from '../../utils/fontHelper';
import { useKeyboardAware } from '../../hooks/useKeyboardAware';
import { useReviewDraft, formatRelativeTime } from '../../hooks/useReviewDraft';
import { getStandardizedMovieId } from '@watchlist/shared';
import {
  hapticSuccess,
  hapticWarning,
  hapticError,
  hapticLight,
  hapticSelectionChanged,
} from '../../utils/nativeHaptics';
import WriteReviewDialog from './WriteReviewDialog';
import {
  createStory,
  deleteStoryForReview,
} from '../../services/storiesService';
import { useUserProfileCache } from '../../contexts/UserProfileCacheContext';

const normalizeDateOnly = (date: Date): Date => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

interface MarkAsWatchedDialogProps {
  open: boolean;
  onClose: () => void;
  movie: TMDbMovie;
  user?: User;
  existingRating?: number;
  existingReview?: string;
  existingWatchedDate?: Date;
  onMarkAsWatched: (
    rating?: number,
    review?: string,
    isPrivate?: boolean,
    visibility?: 'anyone' | 'only-me',
    watchedDate?: Date | null,
    watchedDatePrecision?: WatchedDatePrecision,
    shareToFeed?: boolean
  ) => void;
  onDeleteRatingReview?: () => void;
  zIndex?: number;
  /** Callback when draft is saved (for showing in reviews list) */
  onDraftSaved?: () => void;
  /** Callback when draft is discarded (for clearing from reviews list) */
  onDraftDiscarded?: () => void;
  /** When true, automatically opens the WriteReviewDialog on mount/open. */
  autoOpenWriteReview?: boolean;
}

type FeedbackKind = 'success' | 'info' | 'warning' | 'error';
interface FeedbackState {
  message: string;
  severity: FeedbackKind;
}

const MarkAsWatchedDialog: React.FC<MarkAsWatchedDialogProps> = ({
  open,
  onClose,
  movie,
  user,
  existingRating,
  existingReview,
  existingWatchedDate,
  onMarkAsWatched,
  onDeleteRatingReview,
  zIndex,
  onDraftSaved,
  onDraftDiscarded,
  autoOpenWriteReview,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isKeyboardOpen, bottomPadding, scrollContainerRef } = useKeyboardAware();
  const { getUserProfile } = useUserProfileCache();
  const currentUserProfile = user ? getUserProfile(user.uid) : null;
  const isProfilePrivate = currentUserProfile?.privacy?.profileVisibility === 'private';
  const publicLabel = isProfilePrivate ? 'Followers' : 'Public';

  // Form state
  const [localRating, setLocalRating] = useState<number | null>(existingRating || null);
  const [localReview, setLocalReview] = useState(existingReview || '');
  const [visibility, setVisibility] = useState<'anyone' | 'only-me'>('anyone');
  const todayAtMidnight = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
  const [watchedDate, setWatchedDate] = useState<Date>(todayAtMidnight);
  const [watchedPrecision, setWatchedPrecision] = useState<WatchedDatePrecision>('day');
  const [writeReviewOpen, setWriteReviewOpen] = useState(false);
  const [postAsStory, setPostAsStory] = useState(true);
  const [shareToFeed, setShareToFeed] = useState(!(existingRating || existingReview));

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Feedback (toast + haptic)
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);
  const showFeedback = useCallback((message: string, severity: FeedbackKind, haptic?: () => Promise<void>) => {
    setFeedback({ message, severity });
    void haptic?.();
  }, []);

  const [displayMovie, setDisplayMovie] = useState<TMDbMovie>(movie);
  const standardizedMovieId = React.useMemo(() => getStandardizedMovieId(movie), [movie]);

  // Draft management
  const draftHook = useReviewDraft(user?.uid || '');
  const [lastSavedDisplay, setLastSavedDisplay] = useState<string>('');
  const isEditMode = !!(existingRating || existingReview || existingWatchedDate);
  const hasPublishedReview = !!existingReview;

  // Update last saved display periodically
  useEffect(() => {
    if (!draftHook.lastSavedAt) {
      setLastSavedDisplay('');
      return;
    }
    const updateDisplay = () => setLastSavedDisplay(formatRelativeTime(draftHook.lastSavedAt));
    updateDisplay();
    const interval = setInterval(updateDisplay, 10000);
    return () => clearInterval(interval);
  }, [draftHook.lastSavedAt]);

  // Reset form on open transition.
  useEffect(() => {
    if (!open) return;
    setLocalRating(existingRating || null);
    setLocalReview(existingReview || '');
    setVisibility('anyone');
    setPostAsStory(true);
    setShareToFeed(!isEditMode);
    setWriteReviewOpen(!!autoOpenWriteReview);

    if (existingWatchedDate) {
      const date = normalizeDateOnly(existingWatchedDate);
      setWatchedDate(date);
      setWatchedPrecision(inferWatchedDatePrecision(date));
    } else {
      const d = new Date(); d.setHours(0, 0, 0, 0);
      setWatchedDate(d);
      setWatchedPrecision('day');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Update displayMovie when movie prop changes
  useEffect(() => {
    setDisplayMovie(movie);
  }, [movie]);

  // Load draft when dialog opens (only for new reviews, not edit mode).
  // Drafts now require review text — useReviewDraft.hasDraftContent enforces this.
  useEffect(() => {
    const loadDraftData = async () => {
      if (!user || !standardizedMovieId || standardizedMovieId === 'unknown' || !open || hasPublishedReview) return;
      try {
        const draft = await draftHook.loadDraft(standardizedMovieId);
        if (!draft) return;
        if (draftHook.isDraftStale(draft)) {
          await draftHook.deleteDraft(standardizedMovieId);
          return;
        }
        // Legacy rating-only drafts (pre-redesign): ignore them — drafts now require review text.
        if (!draft.review?.trim()) return;

        if (draft.rating) setLocalRating(draft.rating);
        if (draft.review) setLocalReview(draft.review);
        if (draft.visibility) setVisibility(draft.visibility);
        if (draft.watchedDate) {
          const d = new Date(draft.watchedDate);
          setWatchedDate(d);
          setWatchedPrecision(draft.watchedDatePrecision ?? inferWatchedDatePrecision(d));
        } else if (draft.watchedYear) {
          // Legacy draft format
          const month = draft.watchedMonth ?? null;
          const d = month ? new Date(draft.watchedYear, month - 1, 1) : new Date(draft.watchedYear, 0, 1);
          setWatchedDate(d);
          setWatchedPrecision(month ? 'month' : 'year');
        }
      } catch (err) {
        console.error('Failed to load draft:', err);
      }
    };
    loadDraftData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, standardizedMovieId, open, isEditMode]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const isPrivate = visibility === 'only-me';
      const storedDate =
        watchedPrecision === 'day' ? watchedDate :
        watchedPrecision === 'month' ? new Date(watchedDate.getFullYear(), watchedDate.getMonth(), 1) :
          new Date(watchedDate.getFullYear(), 0, 1);
      onMarkAsWatched(
        localRating || undefined,
        localReview.trim() || undefined,
        isPrivate,
        visibility,
        storedDate,
        watchedPrecision,
        shareToFeed
      );

      if (user && standardizedMovieId && standardizedMovieId !== 'unknown') {
        try {
          await draftHook.deleteDraft(standardizedMovieId);
        } catch (err) {
          console.error('Failed to delete draft:', err);
        }
      }

      // Story creation — only for reviews with text, when not private, and toggle is ON
      if (user && standardizedMovieId && standardizedMovieId !== 'unknown') {
        const hasText = !!localReview.trim();
        const shouldPost = hasText && postAsStory && !isPrivate;
        if (shouldPost) {
          try {
            await createStory({
              authorUid: user.uid,
              movieId: standardizedMovieId,
              rating: localRating,
              reviewText: localReview.trim(),
              watchedDate: storedDate.toISOString(),
              watchedDatePrecision: watchedPrecision,
            });
          } catch (err) {
            console.error('Failed to create story:', err);
          }
        } else {
          try {
            await deleteStoryForReview(user.uid, standardizedMovieId);
          } catch {
            // No story to delete — ignore
          }
        }
      }

      showFeedback(hasPublishedReview ? 'Review updated' : 'Marked as watched', 'success', hapticSuccess);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirmed = () => {
    setShowDeleteConfirm(false);
    onDeleteRatingReview?.();
    showFeedback('Rating & review removed', 'warning', hapticWarning);
    onClose();
  };

  const handleClearReview = async () => {
    setLocalReview('');
    if (!hasPublishedReview && user && standardizedMovieId && standardizedMovieId !== 'unknown') {
      try {
        await draftHook.deleteDraft(standardizedMovieId);
        onDraftDiscarded?.();
      } catch (err) {
        console.error('Failed to delete draft:', err);
      }
    }
    showFeedback(hasPublishedReview ? 'Review cleared — tap Update to save' : 'Review removed', 'info', hapticLight);
  };

  // Closing the parent dialog discards rating/date if not committed (per plan).
  // Review text is preserved only via explicit "Save draft" inside WriteReviewDialog.
  const handleClose = () => {
    onClose();
  };

  // Slide-to-rate: maps a touch X position across the star row to a rating.
  const starRowRef = useRef<HTMLDivElement | null>(null);
  const lastSlideRatingRef = useRef<number | null>(null);
  const computeRatingFromTouch = useCallback((clientX: number): number | null => {
    const row = starRowRef.current;
    if (!row) return null;
    const rect = row.getBoundingClientRect();
    const x = clientX - rect.left;
    if (rect.width <= 0) return null;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    // 10 half-star buckets (0.5..5.0)
    const halfStep = Math.ceil(ratio * 10);
    const value = Math.max(0.5, Math.min(5, halfStep / 2));
    return value;
  }, []);

  const handleStarTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    const t = e.touches[0];
    if (!t) return;
    const value = computeRatingFromTouch(t.clientX);
    if (value !== null && value !== lastSlideRatingRef.current) {
      lastSlideRatingRef.current = value;
      setLocalRating(value);
      void hapticSelectionChanged();
    }
    e.stopPropagation();
  }, [computeRatingFromTouch]);

  const handleStarTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    const t = e.touches[0];
    if (!t) return;
    const value = computeRatingFromTouch(t.clientX);
    if (value !== null) {
      lastSlideRatingRef.current = value;
      setLocalRating(value);
    }
    e.stopPropagation();
  }, [computeRatingFromTouch]);

  const renderReviewRow = () => (
    <Box
      onClick={() => setWriteReviewOpen(true)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.75,
        borderRadius: 2.5,
        cursor: 'pointer',
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)',
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
        transition: 'background-color 0.15s ease',
        '&:hover': {
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.55)',
        },
      }}
    >
      <NotePencilIcon size={22} color={theme.palette.text.secondary} />
      <Typography sx={{
        flex: 1,
        fontFamily: font(),
        fontSize: 14,
        color: localReview.trim() ? theme.palette.text.primary : theme.palette.text.secondary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {localReview.trim() ? localReview : 'Add a review'}
      </Typography>
      {localReview.trim() ? (
        <IconButton
          aria-label="Clear review"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            void handleClearReview();
          }}
          sx={{ p: 0.5, flexShrink: 0 }}
        >
          <TrashSimpleIcon size={20} color={theme.palette.text.secondary} />
        </IconButton>
      ) : (
        <CaretRightIcon size={20} color={theme.palette.text.secondary} />
      )}
    </Box>
  );

  const renderStarRow = () => (
    <Box
      ref={starRowRef}
      onTouchStart={handleStarTouchStart}
      onTouchMove={handleStarTouchMove}
      sx={{
        display: 'flex',
        gap: { xs: 0.125, sm: 0.25 },
        justifyContent: 'center',
        flexWrap: 'nowrap',
        maxWidth: '100%',
        touchAction: 'pan-x',
        userSelect: 'none',
      }}
    >
      {[...Array(5)].map((_, index) => {
        const starValue = index + 1;
        const isHalfStar = localRating && localRating > index && localRating < starValue;
        const isFullStar = localRating && localRating >= starValue;

        return (
          <Box key={index} sx={{ position: 'relative', flexShrink: 0 }}>
            <IconButton
              onClick={() => setLocalRating(starValue)}
              sx={{
                width: { xs: 36, sm: 44 },
                height: { xs: 36, sm: 44 },
                minWidth: { xs: 32, sm: 40 },
                p: 0,
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.15s ease',
              }}
            >
              {isFullStar ? (
                <StarIcon size={44} color={theme.palette.mode === 'dark' ? 'rgba(214, 181, 30, 0.9)' : 'rgba(168, 142, 22, 0.9)'} weight="fill" />
              ) : isHalfStar ? (
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ position: 'absolute', display: 'flex' }}><StarIcon size={44} color={theme.palette.mode === 'dark' ? 'rgba(214, 181, 30, 0.9)' : 'rgba(54, 52, 163, 0.9)'} weight="regular" /></Box>
                  <Box sx={{ clipPath: 'inset(0 50% 0 0)', display: 'flex' }}>
                    <StarIcon size={44} weight="fill" color={theme.palette.mode === 'dark' ? 'rgba(214, 181, 30, 0.9)' : 'rgba(54, 52, 163, 0.9)'} />
                  </Box>
                </Box>
              ) : (
                <StarIcon size={44} weight="regular" color={theme.palette.mode === 'dark' ? 'rgba(125, 122, 255, 0.6)' : 'rgba(54, 52, 163, 0.6)'} />
              )}
            </IconButton>
            {/* Half-star tap area (non-touch fallback) */}
            <Box
              onClick={() => setLocalRating(index + 0.5)}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '50%',
                height: '100%',
                cursor: 'pointer',
                zIndex: 2,
              }}
            />
          </Box>
        );
      })}
    </Box>
  );

  const renderMovieCard = () => {
    const subtitle = [
      displayMovie.Year || new Date().getFullYear().toString(),
      displayMovie.Genre && displayMovie.Genre !== 'N/A' && displayMovie.Genre.trim()
        ? displayMovie.Genre.split(',')[0]
        : null,
      displayMovie.Runtime && displayMovie.Runtime !== 'N/A'
        ? displayMovie.Runtime
        : (displayMovie.runtime && typeof displayMovie.runtime === 'number' && displayMovie.runtime > 0)
          ? `${Math.floor(displayMovie.runtime / 60)}h ${displayMovie.runtime % 60}m`
          : null,
    ].filter(Boolean).join(' ‧ ');
    return (
      <Box sx={{ mb: 2 }}>
        <CompactMovieCard
          poster={displayMovie.Poster && displayMovie.Poster !== 'N/A' ? tmdbService.getHighQualityPoster(displayMovie.Poster) : null}
          title={displayMovie.Title}
          year={subtitle}
        />
      </Box>
    );
  };

  const renderActivityFeedToggle = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1.75,
        py: 1.25,
        borderRadius: 2.5,
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)',
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <DynamicFeed sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
        <Box>
          <Typography sx={{ fontFamily: font(), fontSize: 14, color: theme.palette.text.primary, fontWeight: 500 }}>
            Share to activity feed
          </Typography>
          {isEditMode && shareToFeed && (
            <Typography sx={{ fontFamily: font(), fontSize: 11, color: theme.palette.text.secondary }}>
              Creates a new activity post
            </Typography>
          )}
        </Box>
      </Box>
      <Switch
        checked={shareToFeed}
        onChange={(e) => {
          setShareToFeed(e.target.checked);
          void hapticSelectionChanged();
        }}
        size="small"
        color="primary"
      />
    </Box>
  );

  const renderStoryToggle = () => {
    if (!localReview.trim()) return null;
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.75,
          py: 1.25,
          borderRadius: 2.5,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)',
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <AutoStories sx={{ fontSize: 20, color: postAsStory ? theme.palette.primary.main : theme.palette.text.secondary }} />
          <Box>
            <Typography sx={{ fontFamily: font(), fontSize: 14, color: theme.palette.text.primary, fontWeight: 500 }}>
              Post as spotlight
            </Typography>
            <Typography sx={{ fontFamily: font(), fontSize: 11, color: theme.palette.text.secondary }}>
              Visible to your crew for 7 days
            </Typography>
          </Box>
        </Box>
        <Switch
          checked={postAsStory}
          onChange={(e) => setPostAsStory(e.target.checked)}
          size="small"
          color="primary"
        />
      </Box>
    );
  };

  const renderSectionLabel = (text: string) => (
    <Typography sx={{
      fontFamily: font(),
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: '0.6px',
      textTransform: 'uppercase',
      color: theme.palette.text.secondary,
      mb: 1,
    }}>
      {text}
    </Typography>
  );

  const renderRatingSection = () => (
    <Box sx={{
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)',
      borderRadius: 3,
      p: { xs: 1.5, sm: 2 },
      display: 'flex',
      justifyContent: 'center',
    }}>
      {renderStarRow()}
    </Box>
  );

  const renderVisibilitySelect = () => (
    <FormControl fullWidth>
      <Select
        value={visibility}
        onChange={(e) => setVisibility(e.target.value as 'anyone' | 'only-me')}
        MenuProps={{ sx: { zIndex: (zIndex || 1200) + 200 } }}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {selected === 'anyone' ? (
              <GlobeIcon size={18} color={theme.palette.text.secondary} />
            ) : (
              <LockIcon size={18} color={theme.palette.text.secondary} />
            )}
            <span>{selected === 'anyone' ? publicLabel : 'Only me'}</span>
          </Box>
        )}
        sx={{
          borderRadius: 2.5,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)',
          minHeight: 44,
          fontFamily: font(),
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.mode === 'dark' ? 'rgba(125, 122, 255, 0.9)' : 'rgba(54, 52, 163, 0.9)',
          },
          '& .MuiSelect-select': {
            fontFamily: font(),
            fontSize: 15,
            color: theme.palette.text.primary,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
          },
        }}
      >
        <MenuItem value="anyone" sx={{ fontFamily: font(), display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <GlobeIcon size={18} color={theme.palette.text.secondary} />
          {publicLabel}
        </MenuItem>
        <MenuItem value="only-me" sx={{ fontFamily: font(), display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LockIcon size={18} color={theme.palette.text.secondary} />
          Only me
        </MenuItem>
      </Select>
    </FormControl>
  );

  const renderHeader = () => (
    <BottomSheetDraggableHeader sx={{
      px: 3,
      pt: 2.5,
      pb: 1.5,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}>
      <Box>
        <Typography sx={{
          fontWeight: 600,
          fontFamily: font(),
          color: theme.palette.text.primary,
          fontSize: 18,
        }}>
          Mark as watched
        </Typography>
        {lastSavedDisplay && !isEditMode && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
            {draftHook.isSaving ? (
              <CloudQueue sx={{ fontSize: 13, color: theme.palette.text.secondary }} />
            ) : (
              <CloudDone sx={{ fontSize: 13, color: theme.palette.text.secondary }} />
            )}
            <Typography sx={{ fontSize: 11, color: theme.palette.text.secondary, fontFamily: font() }}>
              {draftHook.isSaving ? 'Saving...' : `Saved ${lastSavedDisplay}`}
            </Typography>
          </Box>
        )}
      </Box>
      <IconButton
        onClick={handleClose}
        sx={{
          width: 36,
          height: 36,
          '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' },
        }}
      >
        <XIcon size={20} color={theme.palette.text.primary} />
      </IconButton>
    </BottomSheetDraggableHeader>
  );

  const renderFooterActions = () => (
    <Box sx={{
      p: 2,
      flexShrink: 0,
      borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
      pb: isMobile && isKeyboardOpen
        ? `${bottomPadding + 16}px`
        : isMobile
          ? 'calc(var(--safe-area-inset-bottom) + 16px)'
          : 2,
      backdropFilter: 'blur(20px)',
      bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)',
    }}>
      {isEditMode ? (
        <Box sx={{ display: 'flex', gap: 2 }}>
          {onDeleteRatingReview && (
            <CustomButton
              semantic="tertiary"
              onClick={handleDelete}
              disabled={isSubmitting}
              sx={{
                flex: 1,
                height: 44,
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: '0.8px',
                textTransform: 'none',
                borderRadius: 2.5,
              }}
            >
              Delete
            </CustomButton>
          )}
          <CustomButton
            semantic="brand"
            onClick={handleSubmit}
            loading={isSubmitting}
            sx={{
              flex: 1,
              height: 44,
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: '0.5px',
              textTransform: 'none',
              borderRadius: 2.5,
            }}
          >
            Update
          </CustomButton>
        </Box>
      ) : (
        <CustomButton
          semantic="brand"
          onClick={handleSubmit}
          loading={isSubmitting}
          sx={{
            width: '100%',
            height: 44,
            backgroundColor: '#ff6482',
            color: 'rgba(0, 0, 0, 0.9)',
            fontWeight: 600,
            fontSize: 14,
            letterSpacing: '0.5px',
            textTransform: 'none',
            borderRadius: 2.5,
            border: '1px solid rgba(255,255,255,0.1)',
            '&:hover': { backgroundColor: '#e55a75' },
          }}
        >
          Mark as watched
        </CustomButton>
      )}
    </Box>
  );

  const renderBody = () => (
    <Box
      ref={scrollContainerRef as React.RefObject<HTMLDivElement>}
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-y',
        px: 3,
        pt: 1,
        pb: isMobile && isKeyboardOpen ? `${bottomPadding + 24}px` : 2,
      }}
    >
      {renderMovieCard()}
      <Box sx={{ mb: 1 }}>
        {renderRatingSection()}
      </Box>
      <Box sx={{ mb: localReview.trim() ? 3.5 : 3.5 }}>
        {renderReviewRow()}
      </Box>
      <Box sx={{ mb: 3.5 }}>
        {renderSectionLabel('When did you watch it')}
        <WatchedDateSelector
          value={watchedDate}
          precision={watchedPrecision}
          onChange={(date, precision) => { setWatchedDate(date); setWatchedPrecision(precision); }}
          movieReleaseYear={displayMovie.Year ? parseInt(displayMovie.Year) : undefined}
        />
      </Box>
      <Box sx={{ mb: 1 }}>
        {renderSectionLabel('Visibility')}
        {renderVisibilitySelect()}
      </Box>
      <Box sx={{ mb: 1 }}>
        {renderActivityFeedToggle()}
      </Box>
      {localReview.trim() && (
        <Box sx={{ mb: 2.5 }}>
          {renderStoryToggle()}
        </Box>
      )}
    </Box>
  );

  // WriteReviewDialog feedback bridge
  const handleWriteReviewFeedback = useCallback((
    kind: 'review-added' | 'draft-saved' | 'draft-discarded' | 'error',
    message?: string
  ) => {
    switch (kind) {
      case 'review-added':
        showFeedback('Review added — tap Mark as watched to publish', 'info', hapticLight);
        break;
      case 'draft-saved':
        showFeedback('Draft saved', 'success', hapticLight);
        break;
      case 'draft-discarded':
        showFeedback('Draft discarded', 'warning', hapticWarning);
        break;
      case 'error':
        showFeedback(message || "Couldn't save", 'error', hapticError);
        break;
    }
  }, [showFeedback]);

  const writeReviewDialog = user && (
    <WriteReviewDialog
      open={writeReviewOpen}
      onClose={() => setWriteReviewOpen(false)}
      initialReview={localReview}
      movie={displayMovie}
      userId={user.uid}
      standardizedMovieId={standardizedMovieId}
      onSave={(review) => setLocalReview(review)}
      onDraftSaved={onDraftSaved}
      onDraftDiscarded={onDraftDiscarded}
      onFeedback={handleWriteReviewFeedback}
      isEditMode={hasPublishedReview}
      draftContext={{
        rating: localRating,
        visibility,
        watchedDate: watchedDate.toISOString(),
        watchedDatePrecision: watchedPrecision,
      }}
      zIndex={(zIndex || 1200) + 100}
    />
  );

  const feedbackSnackbar = (
    <Snackbar
      open={!!feedback}
      autoHideDuration={feedback?.severity === 'error' ? 6000 : feedback?.severity === 'warning' ? 5000 : 3000}
      onClose={() => setFeedback(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      {feedback ? (
        <Alert
          severity={feedback.severity}
          onClose={() => setFeedback(null)}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setFeedback(null)}
              sx={{ fontFamily: font(), fontWeight: 600 }}
            >
              Dismiss
            </Button>
          }
        >
          {feedback.message}
        </Alert>
      ) : undefined}
    </Snackbar>
  );

  const deleteConfirmDialog = (
    <Dialog
      open={showDeleteConfirm}
      onClose={() => setShowDeleteConfirm(false)}
      PaperProps={{ sx: { ...glassStyles.popup(theme.palette.mode === 'dark'), borderRadius: 3, p: 1 } }}
      sx={{ zIndex: (zIndex || 1200) + 200 }}
    >
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography variant="h6" sx={{ fontFamily: font(), fontWeight: 700, mb: 1 }}>
          Delete Review?
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: font(), color: theme.palette.text.secondary }}>
          This will permanently delete your rating and review. This cannot be undone.
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5, p: 2, pt: 1.5 }}>
        <CustomButton
          semantic="tertiary"
          onClick={() => setShowDeleteConfirm(false)}
          sx={{ flex: 1, height: 44, fontFamily: font(), fontWeight: 600, fontSize: 14, textTransform: 'none', borderRadius: 2.5 }}
        >
          Cancel
        </CustomButton>
        <CustomButton
          semantic="brand"
          onClick={handleDeleteConfirmed}
          sx={{ flex: 1, height: 44, fontFamily: font(), fontWeight: 600, fontSize: 14, textTransform: 'none', borderRadius: 2.5, bgcolor: theme.palette.error.main, '&:hover': { bgcolor: theme.palette.error.dark } }}
        >
          Delete
        </CustomButton>
      </Box>
    </Dialog>
  );

  if (isMobile) {
    return (
      <>
        <BottomSheet
          open={open}
          onClose={handleClose}
          maxHeight="90dvh"
          zIndex={zIndex || 1200}
          modalProps={{
            disableEnforceFocus: true,
            disableAutoFocus: true,
            disableRestoreFocus: true,
          }}
        >
          {renderHeader()}
          {renderBody()}
          {renderFooterActions()}
        </BottomSheet>
        {writeReviewDialog}
        {deleteConfirmDialog}
        {feedbackSnackbar}
      </>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '80dvh',
            display: 'flex',
            flexDirection: 'column',
            ...glassStyles.popup(theme.palette.mode === 'dark'),
          },
        }}
      >
        {renderHeader()}
        {renderBody()}
        {renderFooterActions()}
      </Dialog>
      {writeReviewDialog}
      {deleteConfirmDialog}
      {feedbackSnackbar}
    </>
  );
};

export default MarkAsWatchedDialog;
